import { SetVal, SetValParam } from '../cpts/inputs/_input.types';

export function toAlphanumericCamelCase(text: string): string {
  // Remove all non-alphanumeric characters.
  const regex = new RegExp(/[^\w]/g);
  text = text.replace(regex, '');
  text = text.toLowerCase();
  // Capitalize the first letter of the string.
  text = (text[0] || '').toUpperCase() + text.slice(1);
  return text;
}

export function asDate(value?: string | number) {
  if (undefined === value) return '';
  const date = new Date(value);
  if (Object.prototype.toString.call(date) === '[object Date]') {
    // it is a date
    if (isNaN(date.getTime())) {
      // d.valueOf() could also work
      // date is not valid
      return value;
    } else {
      // date is valid
      return date.toLocaleString();
    }
  } else {
    // not a date
    return value;
  }
}

// export type MultiCSS = { [styleName: string]: CSSProperties }; no; it can't autocomplete.

export function getEmptyObject() {
  return {};
}

export function missingPropsCSV(obj: { [propName: string]: any }) {
  const missing = [];
  for (const key in obj) {
    if (undefined === obj[key]) {
      missing.push(key);
    }
  }
  if (missing.length) return missing.map((s) => `"${s}"`).join(', ');
  else return false;
}

export function requireAllProps(
  propsObj: { [propName: string]: any },
  objTitle: string
) {
  const missing = missingPropsCSV(propsObj);
  if (missing) {
    throw new Error(
      `The following properties are missing in an object titled "${objTitle}": [${missing}]`
    );
  }
}

export function timeout<T>(ms: number, promise: Promise<T>) {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`TIMEOUT: duration exceeded ${ms / 1000}s`));
    }, ms);

    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((reason) => {
        clearTimeout(timer);
        reject(reason);
      });
  });
}

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

function checkIfBackToSafetyIsMandatory() {
  return false; // TODO: make this reference userRoles and/or app configs
}
export function backToSafety(msg: string, safeUrl: string = '/') {
  console.error('backToSafety: ', msg, new Error());
  alert(msg);
  if (
    checkIfBackToSafetyIsMandatory() ||
    // eslint-disable-next-line no-restricted-globals
    confirm('Proceed back to safety? (strongly recommended)')
  ) {
    alert("You'll be redirected home now."); // TODO: don't do this interaction in 3 separate dialogs; condense a single message for a single alert/prompt dialog.
    if (safeUrl) {
      window.location.href = safeUrl;
    } else {
      history.back(); /* eslint-disable-line */
    }
  }
  // TODO: implement this over the "variant" (and any other) redirects. (Or delete "variants" since it seems like we're not using them?)
}

export function alertAndThrow(msg: string) {
  alert(msg);
  throw new Error(msg);
}

const oopsMsg =
  'Oops... there was an error loading the form. Please notify your system administrator: \n\nERROR: ';

export function getRequiredProp(object: { [key: string]: any }, key: string) {
  const val = object[key];
  if (val) {
    return val;
  } else {
    backToSafety(`${oopsMsg} data-property '${key}' was not found.)`);
  }
}

// export function filterObjectByKeys<T extends object>(
//   obj: T,
//   includeKeys: string[] = [],
//   excludeKeys: string[] = []
// ): Partial<T> {
//   return Object.keys(obj)
//     .filter(key =>
//       (includeKeys.length === 0 || includeKeys.includes(key)) &&
//       !excludeKeys.includes(key)
//     )
//     .reduce((acc, key) => {
//       acc[key as keyof T] = obj[key as keyof T];
//       return acc;
//     }, {} as Partial<T>);
// }

// COPYPASTA GEN1-GEN2 GENERICS
export function prettyDate() {
  return new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}
export function YyyyMmDd(dateParam: any) {
  return new Date(dateParam || Date.now()).toISOString().split('T')[0];
}
export function pad2(n: number) {
  return n < 10 ? '0' + n : n;
}
export function Yyyy_Mm_Dd_Hh_Mm_Ss(date = new Date()) {
  return (
    date.getFullYear().toString() +
    '-' +
    pad2(date.getMonth() + 1) +
    '-' +
    pad2(date.getDate()) +
    ' ' +
    pad2(date.getHours()) +
    ':' +
    pad2(date.getMinutes()) +
    ':' +
    pad2(date.getSeconds())
  );
}

export function Yyyy_Mm_Dd__Hh_Mm_Ss__forRealThough(date = new Date()) {
  return (
    date.getFullYear().toString() +
    '_' +
    pad2(date.getMonth() + 1) +
    '_' +
    pad2(date.getDate()) +
    '__' +
    pad2(date.getHours()) +
    '_' +
    pad2(date.getMinutes()) +
    '_' +
    pad2(date.getSeconds())
  );
}

export const MS_PER_YEAR = 1000 * 60 * 60 * 24 * 365.25;
// END COPYPASTA GEN1-GEN2 GENERICS

// https://stackoverflow.com/a/52687077/1417853
export function getSerializedNavigator(): string {
  return JSON.stringify(recur(navigator));
}
function recur(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  let _tmp: Record<string, any>;
  for (const i in obj) {
    if (i === 'enabledPlugin' || typeof obj[i] === 'function') {
      continue;
    } else if (typeof obj[i] === 'object' && obj[i] !== null) {
      _tmp = recur(obj[i]);
      if (Object.keys(_tmp).length) {
        result[i] = _tmp;
      }
    } else {
      result[i] = obj[i];
    }
  }
  return result;
}

export const UNKNOWN_UNAME = 'unknown';

export function goHome() {
  window.location.href = '/';
}

export function toPythonLiteral(obj: any): string {
  const replacer = (key: string | null, value: any): string => {
    if (typeof value === 'string') {
      return `'${value.replace(/'/g, "\\'")}'`; // Handle single quotes in strings
    } else if (typeof value === 'boolean') {
      return value ? 'True' : 'False'; // Convert boolean values
    } else if (value === null) {
      return 'None'; // Convert null to None
    } else if (Array.isArray(value)) {
      const arrayContents = value
        .map((item) => replacer(null, item))
        .join(', ');
      return `[${arrayContents}]`; // Recursively format arrays
    } else if (typeof value === 'object' && value !== null) {
      const objectContents = Object.entries(value)
        .map(([key, val]) => {
          return `'${key}': ${replacer(null, val)}`; // Recursively format object
        })
        .join(', ');
      return `{${objectContents}}`;
    }
    return value.toString(); // Convert numbers and any other types to string
  };

  return replacer(null, obj);
}

// fail:
// function toPythonLiteral(obj: any, indentLevel: number = 0): string {
//   // Define the indentation (4 spaces here, but could be a tab '\t')
//   const indent = ' '.repeat(indentLevel * 4);

//   if (typeof obj === 'object' && !Array.isArray(obj) && obj !== null) {
//       const items = Object.entries(obj).map(([key, value]) => {
//           return `${indent}    '${key}': ${toPythonLiteral(value, indentLevel + 1)}`;
//       });
//       return `{\n${items.join(',\n')}\n${indent}}`;
//   } else if (Array.isArray(obj)) {
//       const items = obj.map(item => `${indent}    ${toPythonLiteral(item, indentLevel + 1)}`);
//       return `[\n${items.join(',\n')}\n${indent}]`;
//   }
//   // Handle other types (strings, numbers, booleans) without indentation
//   // Your existing toPythonLiteral logic here
// }

export function omitBloat(obj: any) {
  return {
    ...obj,
    folderLatestNoteVals: 'OMITTED',
    noteFreshFolder: 'OMITTED',
    userAuth: 'OMITTED',
  };
}

export function sortObjectKeys<T extends object>(obj: T) {
  return Object.keys(obj)
    .sort()
    .reduce((acc, key) => {
      acc[key as keyof T] = obj[key as keyof T];
      return acc;
    }, {} as T);
}
export function sortObjectKeysRecursively<T extends Record<string, any>>(
  obj: T
): T {
  const sortedObject = Object.keys(obj)
    .sort()
    .reduce((acc: Record<string, any>, key: string) => {
      const value = obj[key];
      // Check if the value is an object and not null or an array
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        // Recursively sort the object values
        acc[key] = sortObjectKeysRecursively(value as Record<string, any>);
      } else {
        // Directly assign all other values
        acc[key] = value;
      }
      return acc;
    }, {});

  return sortedObject as T;
}

export function createTracedSetState<T = any>(
  setState: SetVal<T>,
  name: string
) {
  return function (newValue: SetValParam<T>) {
    console.log(`Setting in "${name}" with value:`, newValue);
    console.trace('Stack trace for setState in ' + name);
    setState(newValue);
  };
}
// e.g.:
// const auditedSetFormData = useMemo(() => {
//   return createTracedSetState(setFormData, 'FamilyEngagementForm setFormData');
// }, []);

export function getDateFormats(date: Date = new Date()) {
  // CA locale uses YYYY-MM-DD format
  const YyyyMmDd = date.toLocaleDateString('en-CA');

  // GB locale gives 24-hour format without seconds
  const Hh_Mm = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return {
    YyyyMmDd,
    Hh_Mm,
  };
}

export const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};
export const copyJSONToClipboard = (obj: any) => {
  copyToClipboard(JSON.stringify(obj, null, 2));
};

export function areDeepEqual(obj1: any, obj2: any): boolean {
  // Check for strict equality
  if (obj1 === obj2) return true;

  // Handle null and non-object types
  if (
    obj1 === null ||
    obj2 === null ||
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object'
  ) {
    return false;
  }

  // Handle Date objects
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }

  // Handle Array objects
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!areDeepEqual(obj1[i], obj2[i])) return false;
    }
    return true;
  }

  // Handle generic objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key) || !areDeepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

export function isEmail(emailAdress: string) {
  let regex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
  return !!regex.test(emailAdress.toLowerCase());
}

export function arraysOverlap(a: any[], b: any[]) {
  return a.some((aEl) => b.includes(aEl));
}

export function isStringSafe(str: string) {
  // Allow all Unicode letters, numbers, spaces, plus specific punctuation, including curly quotes
  // Removed underscore (_) from allowed characters
  const saferCharsRegex = /^[\p{L}\p{N}\p{Z}.~()!*:@+?',\-""'']*$/u; // u flag for Unicode support
  return saferCharsRegex.test(str);
}

export function areAllStringPropsSafe_wAlerts(obj: Record<string, unknown>) {
  for (const value of Object.values(obj)) {
    if (value && 'string' === typeof value && !isStringSafe(value)) {
      // Create a simpler regex just for finding invalid characters (no start/end anchors)
      const validCharsRegex = /[\p{L}\p{N}\p{Z}.~()!*:@+?',\-""'']/u;
      const allChars = value.split('');
      const invalidChars = allChars.filter(char => !validCharsRegex.test(char));
      
      // Create user-friendly message with specific invalid characters
      const invalidCharsStr = [...new Set(invalidChars)].join(' ');
      alert(
        `Oops... "${value}" contains invalid characters: ${invalidCharsStr}\n\n` +
        `Please use only letters, numbers, spaces, and the following special characters: ' ' " " . ~ ( ) ! * : @ + ? , -`
      );
      return false;
    }
  }
  return true;
}

export function stripHtmlTags(input: string): string {
  // Create a new DOM parser
  const parser = new DOMParser();
  // Parse the string as HTML
  const doc = parser.parseFromString(input, 'text/html');
  // Extract and return the text content
  return doc.body.textContent || '';
}

export function downloadData(
  filename: string,
  data: string,
  mimeType = 'text/plain'
) {
  const blob = new Blob([data], { type: mimeType });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

export function downloadJSONData(filename: string, data: any) {
  downloadData(filename, JSON.stringify(data, null, 2), 'application/json');
}

/**
 * Calculates the maximum number of characters that can be stored in utf16 (e.g. LocalStorage) text
 * given a storage size in megabytes.
 *
 * @param {number} sizeMB - The desired storage size in megabytes.
 * @returns {number} - The maximum number of characters that can be stored.
 */
const BYTES_PER_MB = 1024 * 1024;
export function mbToNumUtf16Chars(sizeMB: number): number {
  const BYTES_PER_CHAR_IN_JS_UTF16 = 2;
  return (sizeMB * BYTES_PER_MB) / BYTES_PER_CHAR_IN_JS_UTF16;
}
export function charsToMBInUtf16(numChars: number): number {
  const BYTES_PER_CHAR_IN_JS_UTF16 = 2;
  return (numChars * BYTES_PER_CHAR_IN_JS_UTF16) / BYTES_PER_MB;
}
export function getLocalStorageMB() {
  let totalSize = 0;
  for (let key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      let value = localStorage.getItem(key);
      // Multiply by 2 because JavaScript uses UTF-16 (2 bytes per character)
      totalSize += value ? value.length * 2 : 0;
    }
  }
  return totalSize / BYTES_PER_MB;
}

export function isGuid(value: string): boolean {
  try {
    const guidRegex =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
    return guidRegex.test(value);
  } catch (e) {
    return false;
  }
}
